/**
 * SmartReportCard Component (Phase 3 — UX Overhaul)
 *
 * Vertical card that auto-generates a report based on the client's
 * connected platforms. Icon on top, text below, button at bottom.
 */

import { MagicWandIcon } from "@phosphor-icons/react";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import { PortalLightTooltip } from "./PortalLightTooltip";

interface SmartReportCardProps {
  connectedPlatforms: string[];
  onGenerate: () => void;
}

export function SmartReportCard({
  connectedPlatforms,
  onGenerate,
}: SmartReportCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onGenerate}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onGenerate(); } }}
      data-testid="smart-report-card"
      className="relative overflow-hidden rounded-[30px] bg-card px-6 py-6 h-full shadow-sm cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring border-[0.5px] border-[var(--hairline)]"
    >
      <div className="flex flex-col items-center text-center gap-3 h-full">
        <MagicWandIcon size={24} className="text-foreground" />
        <div>
          <h3 className="text-base font-semibold text-foreground">Generate Smart Report</h3>
          <p className="text-sm text-foreground/70 mt-1">
            Auto-create a report based on your connected platforms and key metrics
          </p>
        </div>
        {/* Connected platform logos — same stack as the client selector dropdown */}
        {connectedPlatforms.length > 0 && (
          <div className="flex items-center justify-center flex-nowrap gap-1" data-testid="smart-report-platforms">
            {connectedPlatforms.slice(0, 10).map((platformId) => {
              const platform = PICKER_PLATFORMS.find((p) => p.platform === platformId);
              if (!platform?.domain) return null;
              return (
                <PortalLightTooltip key={platformId} label={platform.displayName}>
                  <img
                    src={logoUrl(platform.domain)}
                    alt={platform.displayName}
                    className="w-5 h-5 rounded-full bg-white ring-[1.5px] ring-white object-contain"
                    loading="lazy"
                  />
                </PortalLightTooltip>
              );
            })}
            {connectedPlatforms.length > 10 && (() => {
              const hidden = connectedPlatforms.slice(10)
                .map((pid) => PICKER_PLATFORMS.find((p) => p.platform === pid)?.displayName)
                .filter((n): n is string => Boolean(n))
                .join(", ");
              return (
                <PortalLightTooltip label={hidden}>
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-background text-[10px] font-medium text-foreground border border-input shadow-xs">
                    +{connectedPlatforms.length - 10}
                  </span>
                </PortalLightTooltip>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
